import React, { useMemo } from "react";
import { Alert } from "antd";

interface TemplatePreviewProps {
  template: any; // Selected template data
  variables: string[] | Record<string, any>; // User-provided values
}

const substituteEmailPlaceholders = (
  source: string,
  values: Record<string, any>,
): string => {
  if (!source) return source;
  return source.replace(/\{\{(\s*[\w.]+\s*)\}\}/g, (match, key) => {
    const trimmed = key.trim();
    if (Object.prototype.hasOwnProperty.call(values, trimmed)) {
      const v = values[trimmed];
      if (v === null || v === undefined || v === "") {
        return `[${trimmed}]`;
      }
      return String(v);
    }
    return `[${trimmed}]`;
  });
};

const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  template,
  variables,
}) => {
  const isEmailTemplate = Boolean(template?.body_html);

  const emailPreview = useMemo(() => {
    if (!isEmailTemplate) return null;
    const valuesMap: Record<string, any> = Array.isArray(variables)
      ? {}
      : (variables as Record<string, any>) || {};
    const renderedSubject = substituteEmailPlaceholders(
      template.subject || "",
      valuesMap,
    );
    const renderedBody = substituteEmailPlaceholders(
      template.body_html || "",
      valuesMap,
    );
    return { renderedSubject, renderedBody };
  }, [isEmailTemplate, template, variables]);

  const previewText = useMemo(() => {
    if (!template?.structure?.components) return "";

    const bodyComponent = template.structure.components.find(
      (comp: any) => comp.type === "BODY"
    );

    if (!bodyComponent?.text) return "";

    let text = bodyComponent.text;

    const variableCount = Object.keys(
      template.structure.variables || {}
    ).length;

    for (let i = 1; i <= variableCount; i++) {
      const placeholder = `{{${i}}}`;
      const value = Array.isArray(variables)
        ? variables[i - 1] || `[Variable ${i}]`
        : `[Variable ${i}]`;
      text = text.replace(new RegExp(placeholder, "g"), value);
    }

    return text;
  }, [template, variables]);

  const headerComponent = useMemo(() => {
    if (!template?.structure?.components) return null;
    return template.structure.components.find(
      (comp: any) => comp.type === "HEADER",
    );
  }, [template]);

  const footerComponent = useMemo(() => {
    if (!template?.structure?.components) return null;
    return template.structure.components.find(
      (comp: any) => comp.type === "FOOTER",
    );
  }, [template]);

  if (!template) return null;

  if (isEmailTemplate && emailPreview) {
    return (
      <div className="email-template-preview" style={{ marginTop: 12 }}>
        <Alert
          type="info"
          message="Email Preview"
          description={
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "#595959",
                  marginBottom: 8,
                }}
              >
                <strong>Subject:</strong> {emailPreview.renderedSubject}
              </div>
              <iframe
                srcDoc={emailPreview.renderedBody}
                sandbox="allow-same-origin"
                title="Email preview"
                style={{
                  width: "100%",
                  minHeight: 400,
                  border: "1px solid #f0f0f0",
                  borderRadius: 6,
                  background: "#fff",
                }}
              />
            </div>
          }
        />
      </div>
    );
  }

  return (
    <div className="whatsapp-template-preview">
      <Alert
        type="info"
        message="Template Preview"
        description={
          <div className="template-preview-content">
            {headerComponent && (
              <div className="template-header">
                {headerComponent.format === "IMAGE" && (
                  <div className="header-image">🖼️ [Image Header]</div>
                )}
                {headerComponent.format === "TEXT" && (
                  <strong>{headerComponent.text}</strong>
                )}
              </div>
            )}

            <div className="template-body">
              {previewText.split("\n").map((line: any, idx: any) => (
                <p key={idx} style={{ margin: "4px 0" }}>
                  {line}
                </p>
              ))}
            </div>

            {footerComponent && (
              <div
                className="template-footer"
                style={{
                  fontSize: "12px",
                  color: "#888",
                  marginTop: "8px",
                }}>
                {footerComponent.text}
              </div>
            )}
          </div>
        }
      />
    </div>
  );
};

export default TemplatePreview;
